import React, { useState } from 'react';
import { Button, Input, Option, Select, Spinner, Switch } from '@material-tailwind/react';
import { PlusIcon, TrashIcon, TagIcon, XIcon, CheckIcon, PencilIcon } from 'lucide-react';
import {
    useCreateSupportCategory,
    useDeleteSupportCategory,
    useGetSupportCategories,
    useUpdateSupportCategory,
} from '../../../custom-hooks/use-support-categories';
import { SUPPORT_TICKET_PRIORITIES_ENUM } from '../../../utils/enums';
import { convertToPresentableText } from '../../../utils/functions';

const EMPTY_DRAFT = { name: '', description: '', defaultPriority: 'medium' };

/**
 * Admin management of the ticket taxonomy — SRD §3 ("Config categories") and §11
 * ("change category taxonomy"). The list drives every category dropdown in the app.
 *
 * `includeInactive` so retired categories stay visible and can be switched back on;
 * they just don't appear on new tickets.
 */
const CategoriesSection = () => {
    const { data: categories = [], isLoading } = useGetSupportCategories({ includeInactive: true });
    const createMutation = useCreateSupportCategory();
    const updateMutation = useUpdateSupportCategory();
    const deleteMutation = useDeleteSupportCategory();

    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);

    const busy = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

    const submitNew = async () => {
        if (!draft.name.trim()) return;
        await createMutation.mutateAsync(draft);
        setDraft(EMPTY_DRAFT);
        setAdding(false);
    };

    const startEdit = (category) => {
        setEditingId(category._id);
        setEditDraft({
            name: category.name,
            description: category.description || '',
            defaultPriority: category.defaultPriority,
        });
    };

    const submitEdit = async (categoryId) => {
        await updateMutation.mutateAsync({ categoryId, data: editDraft });
        setEditingId(null);
    };

    const toggleActive = (category) => updateMutation.mutateAsync({
        categoryId: category._id,
        data: { active: !category.active },
    });

    if (isLoading) {
        return (
            <div className='flex justify-center py-8'>
                <Spinner className='h-6 w-6' />
            </div>
        );
    }

    return (
        <div className='space-y-4'>
            <div className='flex items-center justify-between border-b border-blue-gray-50 pb-2'>
                <div className='flex items-center gap-2 text-blue-gray-800'>
                    <TagIcon size={18} />
                    <h3 className='font-semibold'>Ticket Categories</h3>
                </div>
                <Button
                    size='sm'
                    className='flex items-center gap-2 rounded-full bg-black shadow-none hover:bg-zinc-800'
                    onClick={() => setAdding((v) => !v)}
                    disabled={busy}
                >
                    <PlusIcon size={14} />
                    Add Category
                </Button>
            </div>

            <p className='text-xs text-blue-gray-500'>
                These drive every category dropdown for both staff and members. A new ticket inherits
                its category&rsquo;s default priority unless the person raising it picks one.
            </p>

            {adding && (
                <div className='rounded-xl border border-blue-gray-100 bg-blue-gray-50/40 p-4'>
                    <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
                        <Input
                            label='Name'
                            value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                        <Input
                            label='Description (optional)'
                            value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        />
                        <Select
                            label='Default priority'
                            value={draft.defaultPriority}
                            onChange={(value) => setDraft({ ...draft, defaultPriority: value })}
                            selected={() => convertToPresentableText(draft.defaultPriority)}
                        >
                            {SUPPORT_TICKET_PRIORITIES_ENUM.map((p) => (
                                <Option key={p} value={p}>{convertToPresentableText(p)}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className='mt-3 flex justify-end gap-2'>
                        <Button size='sm' variant='text' onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}>
                            Cancel
                        </Button>
                        <Button size='sm' onClick={submitNew} disabled={!draft.name.trim() || busy}>
                            {createMutation.isLoading ? 'Adding...' : 'Add'}
                        </Button>
                    </div>
                </div>
            )}

            <div className='space-y-2'>
                {categories.map((category) => {
                    const isEditing = editingId === category._id;
                    return (
                        <div
                            key={category._id}
                            className={`rounded-xl border p-3 ${category.active ? 'border-blue-gray-100' : 'border-blue-gray-100 bg-blue-gray-50/50'}`}
                        >
                            {isEditing ? (
                                <div>
                                    <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
                                        <Input
                                            label='Name'
                                            value={editDraft.name}
                                            onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                                        />
                                        <Input
                                            label='Description'
                                            value={editDraft.description}
                                            onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                                        />
                                        <Select
                                            label='Default priority'
                                            value={editDraft.defaultPriority}
                                            onChange={(value) => setEditDraft({ ...editDraft, defaultPriority: value })}
                                            selected={() => convertToPresentableText(editDraft.defaultPriority)}
                                        >
                                            {SUPPORT_TICKET_PRIORITIES_ENUM.map((p) => (
                                                <Option key={p} value={p}>{convertToPresentableText(p)}</Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div className='mt-3 flex justify-end gap-2'>
                                        <Button size='sm' variant='text' onClick={() => setEditingId(null)}>
                                            <XIcon size={14} />
                                        </Button>
                                        <Button size='sm' onClick={() => submitEdit(category._id)} disabled={!editDraft.name.trim() || busy}>
                                            <CheckIcon size={14} />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className='flex flex-wrap items-center justify-between gap-3'>
                                    <div className='min-w-0'>
                                        <div className='flex flex-wrap items-center gap-2'>
                                            <span className={`text-sm font-semibold ${category.active ? 'text-blue-gray-900' : 'text-blue-gray-400'}`}>
                                                {category.name}
                                            </span>
                                            <span className='rounded-full bg-blue-gray-50 px-2 py-0.5 text-[11px] font-medium capitalize text-blue-gray-600'>
                                                {category.defaultPriority}
                                            </span>
                                            {!category.active && (
                                                <span className='rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700'>
                                                    Inactive
                                                </span>
                                            )}
                                        </div>
                                        {category.description && (
                                            <p className='mt-0.5 text-xs text-blue-gray-500'>{category.description}</p>
                                        )}
                                    </div>

                                    <div className='flex shrink-0 items-center gap-1'>
                                        <div className='flex items-center gap-1.5 pr-2'>
                                            <span className='text-[11px] text-blue-gray-500'>Active</span>
                                            <Switch
                                                color='blue'
                                                checked={category.active}
                                                onChange={() => toggleActive(category)}
                                                disabled={busy}
                                            />
                                        </div>
                                        <Button size='sm' variant='text' onClick={() => startEdit(category)} disabled={busy}>
                                            <PencilIcon size={14} />
                                        </Button>
                                        {/* Built-in categories can't be removed — the API rejects it, and the
                                            catch-all in particular has to stay so tickets always have somewhere
                                            to land. Anything still in use is deactivated rather than deleted. */}
                                        {!category.isSystem && (
                                            <Button size='sm' variant='text' color='red' onClick={() => deleteMutation.mutate(category._id)} disabled={busy}>
                                                <TrashIcon size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoriesSection;
